import type { IPreviewSqlVo, ITransformPreviewSqlOptions } from './types';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object';

/** 读取单张 chart 节点上的 SQL 文本，供预览弹窗展示。 */
const readFinalSql = (record: Record<string, unknown>): string => {
  const { final_sql: finalSql } = record;
  return typeof finalSql === 'string' ? finalSql : '';
};

/**
 * 定位当前报表卡片对应的 chart 数据节点。
 *
 * 报表预览 SQL 与图表计算共用接口，响应按 chartId（即 taskId）分组，
 * final_sql 不在顶层，需先找到本卡片节点再取 SQL。
 */
const pickDashboardChartRecord = (
  data: Record<string, unknown>,
  options?: ITransformPreviewSqlOptions,
): Record<string, unknown> | undefined => {
  const { taskId } = options ?? {};

  if (taskId && isRecord(data[taskId])) {
    return data[taskId];
  }
  return undefined;
};

/**
 * 预览 SQL 响应 DTO → 前端 VO。
 *
 * 分析页与报表页最终都通过 `usePreviewSQL` 弹窗展示 SQL，但 wire 结构不同：
 * - 分析页：顶层直接返回 final_sql
 * - 报表页：与图表计算相同，按 chartId 嵌套返回
 *
 * 本方法在 service 边界一次性归一化，调用方只消费 `{ finalSql }`。
 */
export const transformPreviewSqlResponseDtoToVo = (
  data: unknown,
  options?: ITransformPreviewSqlOptions,
): IPreviewSqlVo => {
  if (!isRecord(data)) {
    return { finalSql: '' };
  }

  if ('final_sql' in data) {
    return { finalSql: readFinalSql(data) };
  }

  // 报表中的预览sql结构 比图表编辑中的多了一层 taskId, 所以这里先拆解一下.
  const chartRecord = pickDashboardChartRecord(data, options);
  if (chartRecord) {
    return { finalSql: readFinalSql(chartRecord) };
  }

  return { finalSql: '' };
};
